import { DependencyList, useLayoutEffect } from 'react';
import {
  DEFAULT_BFCACHE_HANDLER_TIMEOUT_MS,
  withBFCacheObservability,
} from './bfcache-observability';
import type {
  ObservableLifecycleOptions,
  InternalLifecycleOptions,
  PageLifecycleEvent,
} from './page-lifecycle-types';

export type PageLifecycleEffectOptions = ObservableLifecycleOptions & {
  label: string;
};

/**
 * Core lifecycle hook that wires pagehide/pageshow handlers with optional observability.
 * Internal implementation shared by usePageLifecycleEffect and usePageRestoreEffect.
 */
export const useBFCacheLifecycle = (
  options: InternalLifecycleOptions,
  deps: DependencyList,
) => {
  useLayoutEffect(() => {
    const { onFreeze, onRestore, timeoutMs, instrumentation } = options;
    const resolvedTimeout = timeoutMs ?? DEFAULT_BFCACHE_HANDLER_TIMEOUT_MS;

    // Wraps handlers with observability when instrumentation is active.
    const wrapHandler = (
      phase: 'freeze' | 'restore',
      handler: (
        event: PageLifecycleEvent,
        signal?: AbortSignal,
      ) => void | Promise<void>,
    ) => {
      if (instrumentation === 'auto') {
        const { label, tags } = options;
        return withBFCacheObservability(handler, {
          label,
          phase,
          timeoutMs: resolvedTimeout,
          extraTags: tags,
        });
      }
      return handler;
    };

    const handlers: Array<{
      event: 'pagehide' | 'pageshow';
      handler: (event: Event) => void;
    }> = [];

    if (onFreeze) {
      handlers.push({
        event: 'pagehide',
        handler: wrapHandler('freeze', onFreeze),
      });
    }

    if (onRestore) {
      handlers.push({
        event: 'pageshow',
        handler: wrapHandler(
          'restore',
          async (event: PageLifecycleEvent, signal?: AbortSignal) => {
            if (!event.persisted) {
              return;
            }
            await onRestore(event, signal);
          },
        ),
      });
    }

    const controller = new AbortController();

    handlers.forEach(({ event, handler }) => {
      window.addEventListener(event, handler, {
        signal: controller.signal,
      });
    });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consumers pass their own dependency arrays to control when handlers are re-registered
  }, deps);
};

/**
 * Page lifecycle hook with observability + timeout enforcement enabled.
 * Choose this when you need telemetry, custom timeouts, or freeze-handling.
 * For simple "run logic on restore" cases, use `usePageRestoreEffect`.
 */
export const usePageLifecycleEffect = (
  options: PageLifecycleEffectOptions,
  deps: DependencyList,
) => {
  useBFCacheLifecycle(
    {
      ...options,
      instrumentation: 'auto',
    },
    deps,
  );
};
